import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import FieldView from 'four-nets/bazaar/filter/field-view';
import FilterView from 'four-nets/bazaar/filter/filter-view';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import defaultStyles from './vendor-county-filter.scss';
import { CatalogCategoriesQuery } from '../vendors.graphql';
import { fromGlobalId, toGlobalId } from 'graphql-relay';
import { useLocation, useHistory } from 'react-router';

export interface Props {
    counties?: CatalogCategoriesQuery['counties'];
    countyIds?: string[];
}

function isMatchingSearch(countyName: string, search: string) {
    if (search.length === 0) {
        return true;
    }

    return countyName.toLowerCase().includes(search.toLowerCase());
}

const VendorCountyFilter: React.FC<Props> = props => {
    const { counties } = props;
    const countyIds = props.countyIds || [];

    const location = useLocation();
    const history = useHistory();

    const [fullText, setFullText] = useState('');
    const [selectedCounties, setSelectedCounties] = useState<Set<string>>(
        new Set(countyIds.map(id => toGlobalId('CountyNode', id)))
    );

    const selectedCountyFields: React.ReactNode[] = [];
    const availableCountyFields: React.ReactNode[] = [];
    let matchFound = false;

    if (!counties) {
        return null;
    }

    counties.edges.forEach(edge => {
        if (!edge || !edge.node) {
            return;
        }

        const county = edge.node;

        if (selectedCounties.has(county.id)) {
            selectedCountyFields.push(
                <FieldView
                    key={county.id}
                    name={county.name}
                    selected={true}
                    pending={false}
                    onClick={() => {
                        setSelectedCounties(prev => {
                            prev.delete(county.id);
                            return new Set(prev);
                        });
                    }}
                />
            );
        } else if (fullText === '' || isMatchingSearch(county.name, fullText)) {
            matchFound = true;
            availableCountyFields.push(
                <FieldView
                    key={county.id}
                    name={county.name}
                    selected={false}
                    pending={false}
                    onClick={() => setSelectedCounties(prev => new Set(prev.add(county.id)))}
                />
            );
        }
    });

    return (
        <FilterView
            style={{
                wrapper: defaultStyles.filterViewWrapper,
                button: defaultStyles.filterViewButton,
                deselect: defaultStyles.filterViewDeselect,
            }}
            title={<FormattedMessage id='LOCATION' />}
            fields={[...selectedCountyFields, ...availableCountyFields]}
            activeFilterFields={
                selectedCounties.size === 0 ? (
                    <FormattedMessage id='LOCATION' />
                ) : (
                    <TextShortener
                        textLength={40}
                        text={counties.edges
                            .filter(edge => edge && edge.node && selectedCounties.has(edge.node.id))
                            .map(edge => edge!.node!.name)
                            .join(', ')}
                    />
                )
            }
            fulltextValue={fullText}
            onFulltextChange={event => setFullText(event.target.value)}
            displayText={!matchFound}
            confirm={() => {
                history.push({
                    pathname: location.pathname,
                    search: `?${queryString.stringify({
                        ...queryString.parse(location.search),
                        county: Array.from(selectedCounties).map(id => fromGlobalId(id).id),
                    })}`,
                });
            }}
            clear={() => {
                setSelectedCounties(new Set());
            }}
        />
    );
};

export default VendorCountyFilter;
